// GlycemicAI Analytics - Firebase Analytics Integration
// Tracks user engagement, feature usage, and key metrics

import { logEvent, setUserProperties, setUserId } from "firebase/analytics";
import { getAnalyticsInstance } from "./firebase";

// Helper to safely log events (handles SSR/unsupported environments)
// Now properly awaits the analytics instance
async function safeLogEvent(eventName: string, params?: Record<string, unknown>) {
  const analytics = await getAnalyticsInstance();
  if (analytics) {
    logEvent(analytics, eventName, params);
  }
}

// ============================================
// USER IDENTIFICATION & PROPERTIES
// ============================================

export async function identifyUser(userId: string) {
  const analytics = await getAnalyticsInstance();
  if (analytics) {
    setUserId(analytics, userId);
  }
}

export async function setUserProperty(name: string, value: string | number | boolean) {
  const analytics = await getAnalyticsInstance();
  if (analytics) {
    setUserProperties(analytics, { [name]: String(value) });
  }
}

// Set multiple user properties at once
export async function setUserProfile(profile: {
  dietType?: string;
  hasAllergies?: boolean;
  streakDays?: number;
  totalQueries?: number;
  badgeCount?: number;
  accountAgedays?: number;
  preferredFeatures?: string[];
}) {
  const analytics = await getAnalyticsInstance();
  if (!analytics) return;

  const properties: Record<string, string> = {};

  if (profile.dietType) properties.diet_type = profile.dietType;
  if (profile.hasAllergies !== undefined) properties.has_allergies = String(profile.hasAllergies);
  if (profile.streakDays !== undefined) properties.streak_days = String(profile.streakDays);
  if (profile.totalQueries !== undefined) properties.total_queries = String(profile.totalQueries);
  if (profile.badgeCount !== undefined) properties.badge_count = String(profile.badgeCount);
  if (profile.accountAgedays !== undefined) properties.account_age_days = String(profile.accountAgedays);
  if (profile.preferredFeatures) properties.preferred_features = profile.preferredFeatures.join(",");

  setUserProperties(analytics, properties);
}

// ============================================
// SESSION & ENGAGEMENT EVENTS
// ============================================

export function trackSessionStart() {
  safeLogEvent("session_start_custom", {
    timestamp: new Date().toISOString(),
    hour_of_day: new Date().getHours(),
    day_of_week: new Date().getDay(),
  });
}

export function trackTabNavigation(tabName: string, previousTab?: string) {
  safeLogEvent("tab_navigation", {
    tab_name: tabName,
    previous_tab: previousTab || "none",
    timestamp: new Date().toISOString(),
  });
}

export function trackFeatureDiscovery(featureName: string) {
  safeLogEvent("feature_discovery", {
    feature_name: featureName,
    timestamp: new Date().toISOString(),
  });
}

// ============================================
// CHAT / AI QUERY EVENTS
// ============================================

export function trackChatQuery(params: {
  queryLength: number;
  activityContext?: string;
  hasImage?: boolean;
  responseTime?: number;
}) {
  safeLogEvent("chat_query_sent", {
    query_length: params.queryLength,
    activity_context: params.activityContext || "general",
    has_image: params.hasImage || false,
    response_time_ms: params.responseTime,
  });
}

export function trackChatResponse(params: {
  responseLength: number;
  hadGiBadges: boolean;
  badgeCount: number;
}) {
  safeLogEvent("chat_response_received", {
    response_length: params.responseLength,
    had_gi_badges: params.hadGiBadges,
    badge_count: params.badgeCount,
  });
}

export function trackFoodSearch(foodName: string, context: string) {
  safeLogEvent("food_search", {
    food_name: foodName.toLowerCase().trim(),
    context: context,
  });
}

// ============================================
// MEAL BUILDER / PLATE VIEW EVENTS
// ============================================

export function trackMealBuild(params: {
  foodCount: number;
  estimatedGI: number;
  estimatedGL: number;
  categories: string[];
}) {
  safeLogEvent("meal_build", {
    food_count: params.foodCount,
    estimated_gi: params.estimatedGI,
    estimated_gl: params.estimatedGL,
    categories: params.categories.join(","),
    gl_level: params.estimatedGL <= 10 ? "low" : params.estimatedGL <= 19 ? "medium" : "high",
  });
}

export function trackMealAnalyzed(params: {
  foodCount: number;
  totalGL: number;
  analysisRequested: boolean;
}) {
  safeLogEvent("meal_analyzed", {
    food_count: params.foodCount,
    total_gl: params.totalGL,
    analysis_requested: params.analysisRequested,
  });
}

// ============================================
// ENERGY ROI / DAILY TRACKING EVENTS
// ============================================

export function trackMealLogged(params: {
  mealDescription: string;
  gi: number;
  gl: number;
  context: string;
  isQuickLog: boolean;
}) {
  safeLogEvent("meal_logged", {
    gi_value: params.gi,
    gl_value: params.gl,
    gl_level: params.gl <= 10 ? "low" : params.gl <= 19 ? "medium" : "high",
    meal_context: params.context,
    is_quick_log: params.isQuickLog,
  });
}

export function trackDailyProgress(params: {
  totalGLPoints: number;
  mealsLogged: number;
  roiPercentage: number;
  budgetStatus: "surplus" | "neutral" | "deficit";
}) {
  safeLogEvent("daily_progress_check", {
    total_gl_points: params.totalGLPoints,
    meals_logged: params.mealsLogged,
    roi_percentage: params.roiPercentage,
    budget_status: params.budgetStatus,
  });
}

export function trackEnergyPortfolioExpanded() {
  safeLogEvent("energy_portfolio_expanded");
}

// ============================================
// MENU DECODER EVENTS
// ============================================

export function trackMenuDecodeStart(inputMethod: "photo" | "text") {
  safeLogEvent("menu_decode_start", {
    input_method: inputMethod,
  });
}

export function trackMenuDecoded(params: {
  itemCount: number;
  lowGLCount: number;
  mediumGLCount: number;
  highGLCount: number;
}) {
  safeLogEvent("menu_decoded", {
    item_count: params.itemCount,
    low_gl_count: params.lowGLCount,
    medium_gl_count: params.mediumGLCount,
    high_gl_count: params.highGLCount,
    low_gl_percentage: Math.round((params.lowGLCount / params.itemCount) * 100),
  });
}

// ============================================
// RECIPE TRANSFORMER EVENTS
// ============================================

export function trackRecipeTransformStart() {
  safeLogEvent("recipe_transform_start");
}

export function trackRecipeTransformed(params: {
  originalGL?: number;
  transformedGL?: number;
  swapCount: number;
}) {
  safeLogEvent("recipe_transformed", {
    original_gl: params.originalGL,
    transformed_gl: params.transformedGL,
    gl_reduction: params.originalGL && params.transformedGL
      ? params.originalGL - params.transformedGL
      : undefined,
    swap_count: params.swapCount,
  });
}

// ============================================
// GROCERY OPTIMIZER EVENTS
// ============================================

export function trackGroceryOptimizeStart() {
  safeLogEvent("grocery_optimize_start");
}

export function trackGroceryOptimized(params: {
  itemCount: number;
  swapSuggestionsCount: number;
}) {
  safeLogEvent("grocery_optimized", {
    item_count: params.itemCount,
    swap_suggestions: params.swapSuggestionsCount,
  });
}

// ============================================
// LEARNING / EDUCATION EVENTS
// ============================================

export function trackLearnSectionViewed(section: string) {
  safeLogEvent("learn_section_viewed", {
    section_name: section,
  });
}

export function trackTipViewed(tipId: string) {
  safeLogEvent("tip_viewed", {
    tip_id: tipId,
  });
}

export function trackFactShared(factId: string, shareMethod: string) {
  safeLogEvent("fact_shared", {
    fact_id: factId,
    share_method: shareMethod,
  });
}

// ============================================
// GAMIFICATION / BADGES EVENTS
// ============================================

export function trackBadgeEarned(badgeId: string, badgeName: string) {
  safeLogEvent("badge_earned", {
    badge_id: badgeId,
    badge_name: badgeName,
  });
}

export function trackStreakUpdate(streakDays: number, isIncrease: boolean) {
  safeLogEvent("streak_update", {
    streak_days: streakDays,
    is_increase: isIncrease,
    milestone: streakDays === 7 || streakDays === 14 || streakDays === 30 || streakDays === 100,
  });
}

// ============================================
// USER PROFILE EVENTS
// ============================================

export function trackProfileUpdated(fields: string[]) {
  safeLogEvent("profile_updated", {
    fields_updated: fields.join(","),
    field_count: fields.length,
  });
}

export function trackDietaryPrefsSet(prefs: {
  dietType?: string;
  allergies?: string[];
}) {
  safeLogEvent("dietary_prefs_set", {
    diet_type: prefs.dietType || "none",
    allergy_count: prefs.allergies?.length || 0,
    has_allergies: (prefs.allergies?.length || 0) > 0,
  });
}

// ============================================
// AUTH EVENTS
// ============================================

export function trackSignUp(method: string) {
  safeLogEvent("sign_up", {
    method: method,
  });
}

export function trackLogin(method: string) {
  safeLogEvent("login", {
    method: method,
  });
}

export function trackLogout() {
  safeLogEvent("logout");
}

// ============================================
// ERROR & PERFORMANCE EVENTS
// ============================================

export function trackError(params: {
  errorType: string;
  errorMessage: string;
  context: string;
}) {
  safeLogEvent("app_error", {
    error_type: params.errorType,
    error_message: params.errorMessage.substring(0, 100), // Truncate long messages
    error_context: params.context,
  });
}

export function trackAIResponseTime(featureName: string, responseTimeMs: number) {
  safeLogEvent("ai_response_time", {
    feature_name: featureName,
    response_time_ms: responseTimeMs,
    is_slow: responseTimeMs > 5000,
  });
}

// ============================================
// FUNNEL TRACKING HELPERS
// ============================================

// Onboarding funnel
export function trackOnboardingStep(step: number, stepName: string, completed: boolean) {
  safeLogEvent("onboarding_step", {
    step_number: step,
    step_name: stepName,
    completed: completed,
  });
}

// Feature engagement funnel
export function trackFeatureFunnel(params: {
  feature: string;
  step: "viewed" | "started" | "completed" | "abandoned";
  metadata?: Record<string, unknown>;
}) {
  safeLogEvent(`feature_${params.step}`, {
    feature_name: params.feature,
    ...params.metadata,
  });
}

// Time-to-first-value tracking
export function trackFirstValue(valueType: string, timeFromSignupMs: number) {
  safeLogEvent("first_value", {
    value_type: valueType,
    time_from_signup_ms: timeFromSignupMs,
    time_from_signup_minutes: Math.round(timeFromSignupMs / 60000),
  });
}

// ============================================
// ENGAGEMENT SIGNALS
// ============================================

export function trackReturnVisit(daysSinceLastVisit: number) {
  safeLogEvent("return_visit", {
    days_since_last_visit: daysSinceLastVisit,
    is_churned_return: daysSinceLastVisit > 7,
  });
}

export function trackScreenTime(screenName: string, durationSeconds: number) {
  safeLogEvent("screen_time", {
    screen_name: screenName,
    duration_seconds: durationSeconds,
    duration_bucket: durationSeconds < 10 ? "short" : durationSeconds < 60 ? "medium" : "long",
  });
}
